import {
  DistributionBoard,
  Job,
  JobProgress,
  JobStatusUpdate,
  JobStepStatus,
  RequestCreateJob,
  RequestCreateOrganisationLocation,
  RequestInviteDriver,
  RequestUpdateJob,
} from '../../app/ApiGen';
import { JobStatus, JobType } from '../../app/enums';
import type { JobWizardState as EvnexJobWizardState } from '../../views/Admin/Jobs/CreateJob/types';
import { removeOptionalValues } from '../formUtils';
import { locationValidationSchema } from '../locations/FieldDefinitions';
import { getIcpNumber } from '../locations/installationConnectionPoint';
import { Normalised } from '../request';
import { evnexJobDetailsSchema, type EvnexJobFormDetails } from './CreateEvnexJob';
import type { JobLocationDetails } from './location';

function buildS3AttachmentUrl(args: { attachment: { fileName: string; signedUrl: string }; jobId: string }): string {
  const { attachment, jobId } = args;
  const baseUrl = attachment.signedUrl.split('.s3')[0];
  return `${baseUrl}.s3.ap-southeast-2.amazonaws.com/jobs/${jobId}/${attachment.fileName}`;
}

export function getLocationAttributes(location: JobLocationDetails) {
  return {
    address: location.address,
    coordinates: location.coordinates,
    icpNumber: getIcpNumber(location.installationPoint),
    name: location.name,
  };
}

export function mapJobWizardStateToRequestCreateJob(
  wizardState: EvnexJobWizardState & { driverId: string; locationId: string },
): RequestCreateJob {
  const { assignedInstallers, contactPhoneNumber, powerSensorCount, referenceId } =
    removeOptionalValues<EvnexJobFormDetails>(wizardState, evnexJobDetailsSchema);
  const {
    driverId,
    locationId,
    locationFieldContents: { timeZone },
  } = wizardState;

  return {
    data: {
      type: 'jobs',
      attributes: {
        contactPhoneNumber,
        details: { timeZone },
        isThirdParty: false,
        jobType: JobType.ResidentialInstall,
        referenceId: referenceId?.trim() ?? undefined,
        distributionBoards: [{ powerSensorCount, chargePointCount: 1 }],
      },
      relationships: {
        assignedInstallers: {
          data: assignedInstallers.map((installer) => ({ type: 'users', id: installer.id })),
        },
        customer: { data: { type: 'users', id: driverId } },
        location: { data: { type: 'locations', id: locationId } },
      },
    },
  };
}

export function mapJobWizardStateToRequestInviteDriver(wizardState: EvnexJobWizardState): RequestInviteDriver {
  const { driverFieldContents } = wizardState;
  return { data: { type: 'users', attributes: { email: driverFieldContents.email, name: driverFieldContents.name } } };
}

export function mapJobWizardStateToRequestCreateLocation(
  wizardState: EvnexJobWizardState,
): RequestCreateOrganisationLocation {
  const { installationPoint, ...locationFieldValues } = wizardState.locationFieldContents;
  const location = { ...locationFieldValues, icpNumber: getIcpNumber(installationPoint) };
  return {
    data: {
      type: 'locations',
      attributes: { ...removeOptionalValues(location, locationValidationSchema), isPublic: false },
    },
  };
}

export function mapJobProgressUpdateToRequestUpdateJob(args: {
  job: { id: string; progress: JobProgress };
  update: {
    commissioningDetails: Omit<DistributionBoard, 'installedChargePoints'> & {
      installedChargePointId: string;
      installerId: string;
    };
    step: { name: keyof JobProgress; status: JobStepStatus };
  };
}): RequestUpdateJob {
  const { job, update } = args;
  const {
    commissioningDetails: { installedChargePointId, installerId, ...distributionBoard },
  } = update;

  const attributesUpdate = {
    details: { progress: { ...job.progress, [update.step.name]: update.step.status } },
    status: JobStatus.InProgress,
  };

  if (update.step.name === 'commissioning' && update.step.status === 'manually completed') {
    return {
      data: {
        id: job.id,
        type: 'jobs',
        attributes: {
          ...attributesUpdate,
          distributionBoards: [{ ...distributionBoard, installedChargePoints: [{ id: installedChargePointId }] }],
        },
        relationships: { installer: { data: { id: installerId, type: 'users' } } },
      },
    };
  }

  return { data: { id: job.id, type: 'jobs', attributes: attributesUpdate } };
}

export function mapJobStatusUpdateToRequestUpdateJob(args: {
  job: Pick<Normalised<Job>, 'details' | 'id'>;
  update: { status: JobStatusUpdate };
}): RequestUpdateJob {
  const { job, update } = args;
  if (update.status === JobStatus.InProgress) {
    return {
      data: {
        id: job.id,
        type: 'jobs',
        attributes: {
          details: {
            progress: {
              preWorkForm: 'ready',
              commissioning: 'not ready',
              postWorkForm: job.details.isAGLJob ? 'not ready' : 'N/A',
              certificateOfCompliance: 'not ready',
            },
          },
          status: update.status,
        },
      },
    };
  }
  return { data: { id: job.id, type: 'jobs', attributes: { status: update.status } } };
}

export function mapCertificateOfComplianceUpdateToRequestUpdateJob(args: {
  attachment: { fileName: string; signedUrl: string };
  job: { id: string; progress: JobProgress | undefined };
}): RequestUpdateJob {
  const { attachment, job } = args;

  if (!job.progress) {
    throw new Error('Job progress data is missing');
  }

  const s3AttachmentUrl = buildS3AttachmentUrl({ attachment, jobId: job.id });
  return {
    data: {
      id: job.id,
      type: 'jobs',
      attributes: {
        details: { progress: { ...job.progress, certificateOfCompliance: 'completed' } },
        status: JobStatus.Complete,
      },
      relationships: {
        attachments: { data: [{ id: s3AttachmentUrl, type: 'attachments' }] },
      },
    },
  };
}
